import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const SolutionSection = () => {
  const [activeFeature, setActiveFeature] = useState(0);

  const features = [
    {
      title: "Zero Marketing Costs",
      description: "We bring premium customers directly to you - no more expensive ads or lead generation",
      icon: "DollarSign",
      color: "primary",
      savings: "$30,000",
      details: [
        "No Google Ads expenses",
        "No Facebook marketing costs", 
        "No lead generation fees",
        "No advertising overhead"
      ],
      testimonial: {
        name: "Mike Rodriguez",
        company: "Rodriguez HVAC",
        quote: "Saved $2,800 monthly on marketing. That's $33,600 back in my pocket every year.",
        increase: "+$33,600 saved"
      }
    },
    {
      title: "Payment Protection",
      description: "Get paid within 24 hours of job completion - guaranteed payment protection",
      icon: "Shield",
      color: "accent",
      savings: "24 Hours",
      details: [
        "Payment within 24 hours",
        "100% payment guarantee",
        "No more chasing invoices",
        "Improved cash flow"
      ],
      testimonial: {
        name: "Sarah Chen",
        company: "Chen Plumbing",
        quote: "No more 60-day payment delays. My cash flow improved dramatically.",
        increase: "+$18,000 cash flow"
      }
    },
    {
      title: "Premium Customers Only",
      description: "Work with verified, high-value customers who respect quality work and pay premium rates",
      icon: "Star",
      color: "secondary",
      savings: "$2,847",
      details: [
        "Pre-screened customers",
        "Higher average job values",
        "Quality-focused clients",
        "Professional relationships"
      ],
      testimonial: {
        name: "David Thompson",
        company: "Thompson Electric",
        quote: "Average job value increased from $1,200 to $2,847. Better customers, better pay.",
        increase: "+137% job value"
      }
    }
  ];

  const comparisonData = [
    {
      category: "Marketing Costs",
      traditional: "$2,500-5,000/month",
      proconnect: "$0/month",
      savings: "Save $30,000-60,000/year"
    },
    {
      category: "Payment Time",
      traditional: "30-90 days",
      proconnect: "24 hours",
      savings: "Improve cash flow"
    },
    {
      category: "Customer Quality",
      traditional: "Mixed quality",
      proconnect: "Premium only",
      savings: "Higher satisfaction"
    },
    {
      category: "Job Value",
      traditional: "$1,200 average",
      proconnect: "$2,847 average",
      savings: "+137% increase"
    }
  ];

  return (
    <section id="benefits" className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
            The ProConnect Solution
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Join Tampa Bay's exclusive contractor network and transform your business 
            with guaranteed customers, protected payments, and zero marketing costs.
          </p>
        </div>

        {/* Interactive Features Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {features?.map((feature, index) => (
            <div
              key={index}
              className={`relative bg-card rounded-xl p-8 border-2 cursor-pointer transition-all duration-300 ${
                activeFeature === index
                  ? `border-${feature?.color} shadow-lg transform -translate-y-2`
                  : 'border-border hover:border-primary/50'
              }`}
              onClick={() => setActiveFeature(index)}
            >
              {/* Feature Icon */}
              <div className={`w-16 h-16 rounded-xl flex items-center justify-center mb-6 ${
                activeFeature === index
                  ? `bg-${feature?.color} text-${feature?.color}-foreground`
                  : 'bg-muted text-foreground'
              }`}>
                <Icon name={feature?.icon} size={32} />
              </div>

              {/* Feature Content */}
              <h3 className="text-xl font-bold text-foreground mb-3">
                {feature?.title}
              </h3>
              <p className="text-muted-foreground mb-6">
                {feature?.description}
              </p>

              {/* Savings Highlight */}
              <div className={`text-center p-4 rounded-lg mb-6 ${
                activeFeature === index
                  ? `bg-${feature?.color}/10 border border-${feature?.color}/20`
                  : 'bg-muted/50'
              }`}>
                <div className={`text-2xl font-bold ${
                  activeFeature === index ? `text-${feature?.color}` : 'text-foreground'
                }`}>
                  {feature?.savings}
                </div>
                <div className="text-sm text-muted-foreground">
                  {index === 0 ? 'Annual Savings' : index === 1 ? 'Payment Time' : 'Avg Job Value'}
                </div>
              </div>

              {/* Feature Details */}
              <div className="space-y-2">
                {feature?.details?.map((detail, detailIndex) => (
                  <div key={detailIndex} className="flex items-center text-sm">
                    <Icon name="Check" size={16} className={`mr-2 ${
                      activeFeature === index ? `text-${feature?.color}` : 'text-accent'
                    }`} />
                    <span className="text-foreground">{detail}</span>
                  </div>
                ))}
              </div>

              {/* Active Indicator */}
              {activeFeature === index && (
                <div className={`absolute top-4 right-4 w-3 h-3 bg-${feature?.color} rounded-full animate-pulse`} />
              )}
            </div>
          ))}
        </div>

        {/* Active Feature Testimonial */}
        <div className="bg-card rounded-xl p-8 border border-border shadow-testimonial mb-16">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mr-4">
                  <Icon name="User" size={24} className="text-foreground" />
                </div>
                <div>
                  <div className="font-semibold text-foreground">
                    {features?.[activeFeature]?.testimonial?.name}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {features?.[activeFeature]?.testimonial?.company}
                  </div>
                </div>
              </div>
              <blockquote className="text-lg text-foreground italic mb-4">
                "{features?.[activeFeature]?.testimonial?.quote}"
              </blockquote>
              <div className="flex items-center">
                {[...Array(5)]?.map((_, i) => (
                  <Icon key={i} name="Star" size={16} className="text-warning mr-1" />
                ))}
                <span className="ml-2 text-sm text-muted-foreground">Verified Contractor</span>
              </div>
            </div>
            <div className="text-center">
              <div className={`text-4xl font-bold text-${features?.[activeFeature]?.color} mb-2`}>
                {features?.[activeFeature]?.testimonial?.increase}
              </div>
              <div className="text-muted-foreground">
                Annual Impact
              </div>
            </div>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="bg-muted/30 rounded-xl p-8">
          <h3 className="text-2xl font-bold text-foreground text-center mb-8">
            Traditional Marketing vs ProConnect
          </h3>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-4 px-4 font-semibold text-foreground">Category</th>
                  <th className="text-center py-4 px-4 font-semibold text-muted-foreground">Traditional</th>
                  <th className="text-center py-4 px-4 font-semibold text-primary">ProConnect</th>
                  <th className="text-center py-4 px-4 font-semibold text-accent">Your Benefit</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData?.map((row, index) => (
                  <tr key={index} className="border-b border-border/50">
                    <td className="py-4 px-4 font-medium text-foreground">{row?.category}</td>
                    <td className="py-4 px-4 text-center text-muted-foreground">{row?.traditional}</td>
                    <td className="py-4 px-4 text-center text-primary font-semibold">{row?.proconnect}</td>
                    <td className="py-4 px-4 text-center text-accent font-semibold">{row?.savings}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Ready to Transform Your Business?
            </h3>
            <p className="text-lg text-muted-foreground mb-6">
              Join 265+ Tampa Bay contractors already earning more with ProConnect
            </p>
            <div className="grid sm:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-2xl font-bold text-primary">$13,000+</div>
                <div className="text-sm text-muted-foreground">Average Annual Increase</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-accent">24 Hours</div>
                <div className="text-sm text-muted-foreground">Payment Guarantee</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-secondary">$0</div>
                <div className="text-sm text-muted-foreground">Marketing Costs</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;